import React, { useMemo, useRef } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import WhatsAppSettings from './WhatsAppSettings';
import WhatsAppTemplates from './WhatsAppTemplates';
import { WhatsAppSettingsProvider } from './WhatsAppSettingsContext';
import PageHeader, { PageCard, PageHeaderBackButton, PageShell } from '../../../common/PageHeader';

const TAB_SETUP = 'setup';
const TAB_TEMPLATES = 'templates';
const FONT = { fontFamily: "'Poppins', sans-serif" };

export default function WhatsAppConfiguration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab === TAB_TEMPLATES ? TAB_TEMPLATES : TAB_SETUP;
  }, [searchParams]);

  const saveBeforeTemplatesRef = useRef(null);

  const handleTabChange = async (_event, value) => {
    if (value === TAB_TEMPLATES && saveBeforeTemplatesRef.current) {
      const result = await saveBeforeTemplatesRef.current();
      if (result?.ok && !result?.skipped) {
        toast.success(
          t('whatsappTemplates.connectionSaved', {
            defaultValue: 'Connection saved. You can add message templates now.',
          })
        );
      } else if (result?.ok === false && result?.error) {
        toast.warning(result.error);
      }
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <WhatsAppSettingsProvider>
      <PageShell fill={false} className="bg-[#f8fafc]" style={FONT}>
        <PageCard overflow={false}>
          <PageHeader
            title={t('whatsappSettings.title')}
            subtitle={t('whatsappSettings.subtitle')}
            leading={<PageHeaderBackButton onClick={() => navigate(-1)} />}
          />
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              minHeight: 48,
              borderBottom: '1px solid #e5e7eb',
              '& .MuiTab-root': {
                ...FONT,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                minHeight: 48,
                color: '#64748b',
                '&.Mui-selected': { color: '#0ea5e9' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#0ea5e9', height: 3 },
            }}
          >
            <Tab value={TAB_SETUP} label={t('whatsappSettings.tabs.connection')} />
            <Tab value={TAB_TEMPLATES} label={t('whatsappSettings.tabs.messageTemplates')} />
          </Tabs>
          <div className="p-3 sm:p-4">
            <div style={{ display: activeTab === TAB_SETUP ? 'block' : 'none' }}>
              <WhatsAppSettings
                registerSaveBeforeTemplates={(fn) => {
                  saveBeforeTemplatesRef.current = fn;
                }}
              />
            </div>
            <div style={{ display: activeTab === TAB_TEMPLATES ? 'block' : 'none' }}>
              <WhatsAppTemplates />
            </div>
          </div>
        </PageCard>
      </PageShell>
    </WhatsAppSettingsProvider>
  );
}
